import { DynamicToolUIPart, UIMessage } from "ai";

import { coerceOptionalJsonValue } from "@/graphql/json-value";

import {
  GraphQLStreamingState,
  GraphQLToolUseState,
  StreamingState,
  ToolUseState,
} from "../enum.types";
import { ConverterError } from "../error";
import { GraphQLUIMessageInput } from "../mutation.types";

type GraphQLUIPartInput = GraphQLUIMessageInput["parts"][number];
type GraphQLTextUIPartInput = NonNullable<GraphQLUIPartInput["text"]>;
type GraphQLReasoningUIPartInput = NonNullable<GraphQLUIPartInput["reasoning"]>;
type GraphQLSourceUrlUIPartInput = NonNullable<GraphQLUIPartInput["sourceUrl"]>;
type GraphQLSourceDocumentUIPartInput = NonNullable<
  GraphQLUIPartInput["sourceDocument"]
>;
type GraphQLFileUIPartInput = NonNullable<GraphQLUIPartInput["file"]>;
type GraphQLDataUIPartInput = NonNullable<GraphQLUIPartInput["data"]>;
type GraphQLToolUIPartInput = NonNullable<GraphQLUIPartInput["tool"]>;
type GraphQLStepStartUIPartInput = NonNullable<GraphQLUIPartInput["stepStart"]>;

type ToolUIPartLike = Extract<
  UIMessage["parts"][number],
  { type: `tool-${string}` }
>;
type ToolUIPartSource = ToolUIPartLike | DynamicToolUIPart;
type ToolApproval = NonNullable<ToolUIPartSource["approval"]>;
type DataUIPartLike = Extract<
  UIMessage["parts"][number],
  { type: `data-${string}` }
>;

export function uiMessageVercelToGql(input: UIMessage): GraphQLUIMessageInput {
  const metadata = coerceOptionalJsonValue(input.metadata) as
    | GraphQLUIMessageInput["metadata"]
    | undefined;
  const parts = partsVercelToGql(input.parts);

  return {
    role: roleVercelToGql(input.role),
    ...(metadata === undefined ? {} : { metadata }),
    parts,
  };
}

function roleVercelToGql(
  role: UIMessage["role"],
): GraphQLUIMessageInput["role"] {
  const map = {
    assistant: "ASSISTANT",
    user: "USER",
    system: "SYSTEM",
  } as const satisfies Record<UIMessage["role"], GraphQLUIMessageInput["role"]>;

  return map[role];
}

function partsVercelToGql(
  parts: UIMessage["parts"],
): GraphQLUIMessageInput["parts"] {
  return parts.map<GraphQLUIPartInput>((part) => {
    if (part.type === "text") {
      const textPart: GraphQLTextUIPartInput = {
        text: part.text,
      };
      const state = streamingStateVercelToGql(part.state);
      if (state !== undefined) textPart.state = state;
      const providerMetadata = coerceOptionalJsonValue(part.providerMetadata);
      if (providerMetadata !== undefined)
        textPart.providerMetadata = providerMetadata;
      return { text: textPart };
    }

    if (part.type === "reasoning") {
      const reasoningPart: GraphQLReasoningUIPartInput = {
        text: part.text,
      };
      const state = streamingStateVercelToGql(part.state);
      if (state !== undefined) reasoningPart.state = state;
      const providerMetadata = coerceOptionalJsonValue(part.providerMetadata);
      if (providerMetadata !== undefined)
        reasoningPart.providerMetadata = providerMetadata;
      return { reasoning: reasoningPart };
    }

    if (part.type === "source-url") {
      const sourceUrlPart: GraphQLSourceUrlUIPartInput = {
        sourceId: part.sourceId,
        url: part.url,
      };
      if (part.title !== undefined) sourceUrlPart.title = part.title;
      const providerMetadata = coerceOptionalJsonValue(part.providerMetadata);
      if (providerMetadata !== undefined)
        sourceUrlPart.providerMetadata = providerMetadata;
      return { sourceUrl: sourceUrlPart };
    }

    if (part.type === "source-document") {
      const sourceDocumentPart: GraphQLSourceDocumentUIPartInput = {
        sourceId: part.sourceId,
        mediaType: part.mediaType,
        title: part.title,
      };
      if (part.filename !== undefined)
        sourceDocumentPart.filename = part.filename;
      const providerMetadata = coerceOptionalJsonValue(part.providerMetadata);
      if (providerMetadata !== undefined)
        sourceDocumentPart.providerMetadata = providerMetadata;
      return { sourceDocument: sourceDocumentPart };
    }

    if (part.type === "file") {
      const filePart: GraphQLFileUIPartInput = {
        mediaType: part.mediaType,
        url: part.url,
      };
      if (part.filename !== undefined) filePart.filename = part.filename;
      const providerMetadata = coerceOptionalJsonValue(part.providerMetadata);
      if (providerMetadata !== undefined)
        filePart.providerMetadata = providerMetadata;
      return { file: filePart };
    }

    // DataUIPart stores provider content without requiring a backend schema migration.
    if (part.type === "custom") {
      return {
        data: {
          type: "custom",
          data: coerceOptionalJsonValue({
            kind: part.kind,
            providerMetadata: part.providerMetadata,
          }),
        },
      };
    }

    if (part.type === "step-start") {
      const stepStartPart: GraphQLStepStartUIPartInput = {};
      return { stepStart: stepStartPart };
    }

    if (part.type.startsWith("data-")) {
      const dataPartSource = part as DataUIPartLike;
      const dataPart: GraphQLDataUIPartInput = {
        type: dataPartSource.type,
      };
      const data = coerceOptionalJsonValue(dataPartSource.data);
      if (data !== undefined) dataPart.data = data;
      if (dataPartSource.id !== undefined) dataPart.id = dataPartSource.id;
      return { data: dataPart };
    }

    if (part.type === "dynamic-tool") {
      return { tool: dynamicToolPartToGql(part) };
    }

    if (part.type.startsWith("tool-")) {
      return { tool: toolPartToGql(part as ToolUIPartLike) };
    }

    throw new ConverterError(`Unsupported UI part type: ${part.type}`);
  });
}

function streamingStateVercelToGql(
  state: StreamingState,
): GraphQLStreamingState | undefined {
  if (!state) return undefined;
  const map = {
    streaming: "STREAMING",
    done: "DONE",
  } as const satisfies Record<
    Exclude<StreamingState, undefined>,
    GraphQLStreamingState
  >;

  return map[state];
}

function toolStateVercelToGql(state: ToolUseState): GraphQLToolUseState {
  if (!state) {
    throw new ConverterError("Unexpected undefined tool state");
  }

  switch (state) {
    case "input-streaming":
      return "INPUT_STREAMING";
    case "input-available":
      return "INPUT_AVAILABLE";
    case "approval-requested":
      return "APPROVAL_REQUESTED";
    case "approval-responded":
      return "APPROVAL_RESPONDED";
    case "output-available":
      return "OUTPUT_AVAILABLE";
    case "output-error":
      return "OUTPUT_ERROR";
    case "output-denied":
      return "OUTPUT_DENIED";
  }
}

function toolPartToGql(part: ToolUIPartLike): GraphQLToolUIPartInput {
  const result: GraphQLToolUIPartInput = {
    toolType: part.type,
    toolCallId: part.toolCallId,
    state: toolStateVercelToGql(part.state),
  };

  return populateToolPartInput(result, part);
}

function dynamicToolPartToGql(part: DynamicToolUIPart): GraphQLToolUIPartInput {
  const result: GraphQLToolUIPartInput = {
    toolType: part.type,
    toolCallId: part.toolCallId,
    state: toolStateVercelToGql(part.state),
    toolName: part.toolName,
  };

  return populateToolPartInput(result, part);
}

function populateToolPartInput(
  result: GraphQLToolUIPartInput,
  part: ToolUIPartSource,
): GraphQLToolUIPartInput {
  if (part.providerExecuted !== undefined)
    result.providerExecuted = part.providerExecuted;
  const inputValue = coerceOptionalJsonValue(part.input);
  if (inputValue !== undefined) result.input = inputValue;
  if (part.state === "output-available") {
    const outputValue = coerceOptionalJsonValue(part.output);
    if (outputValue !== undefined) result.output = outputValue;
  }
  if (part.state === "output-error" && part.errorText !== undefined)
    result.errorText = part.errorText;
  if (
    "callProviderMetadata" in part &&
    part.callProviderMetadata !== undefined
  ) {
    const metadata = coerceOptionalJsonValue(part.callProviderMetadata);
    if (metadata !== undefined) result.callProviderMetadata = metadata;
  }
  if (
    "resultProviderMetadata" in part &&
    part.resultProviderMetadata !== undefined
  ) {
    const metadata = coerceOptionalJsonValue(part.resultProviderMetadata);
    if (metadata !== undefined) result.resultProviderMetadata = metadata;
  }
  if ("approval" in part && part.approval !== undefined) {
    result.approval = approvalVercelToGql(part.approval);
  }
  if ("preliminary" in part && part.preliminary !== undefined)
    result.preliminary = part.preliminary;

  return result;
}

function approvalVercelToGql(
  approval: ToolApproval,
): NonNullable<GraphQLToolUIPartInput["approval"]> {
  const result: NonNullable<GraphQLToolUIPartInput["approval"]> = {
    id: approval.id,
  };

  if ("approved" in approval && approval.approved !== undefined)
    result.approved = approval.approved;
  if ("reason" in approval && approval.reason !== undefined)
    result.reason = approval.reason;
  if (approval.isAutomatic !== undefined)
    result.isAutomatic = approval.isAutomatic;
  if (approval.signature !== undefined) result.signature = approval.signature;

  return result;
}
